import Typography from '@cobliteam/ui/components/Typography';
import { cn } from '@cobliteam/ui/utils';
import { DEV_TOOLS_POSITION_OPTIONS, type DevToolsPosition } from '../lib/devToolsPosition';
import DevToolsPanel from './DevToolsPanel';

interface PositionDataProps {
  position: DevToolsPosition;
  onPositionChange: (position: DevToolsPosition) => void;
}

/** Put each cell's dot in the corner it anchors to, so the grid reads as the screen. */
const getDotAlignment = (position: DevToolsPosition) =>
  cn(position.startsWith('top') ? 'items-start' : 'items-end', position.endsWith('left') ? 'justify-start' : position.endsWith('right') ? 'justify-end' : 'justify-center');

/**
 * Anchoring the toolbar is a spatial choice, so it's picked spatially: the 3x2
 * grid mirrors the screen and the options sit where they'll actually appear. It
 * replaced a `Select`, which also needed a `portal={false}` workaround to escape
 * the dialog.
 *
 * Native radios sharing a `name` give arrow-key navigation and grouping for free.
 */
const PositionData = ({ position, onPositionChange }: PositionDataProps) => {
  const currentLabel = DEV_TOOLS_POSITION_OPTIONS.find((option) => option.value === position)?.label;

  return (
    <DevToolsPanel title='Posição' description='Onde a barra flutuante fica ancorada na tela. A escolha é salva neste navegador.'>
      <fieldset className='flex max-w-sm flex-col gap-2'>
        <legend className='mb-2 text-gray-11 text-xs'>Âncora da barra</legend>

        <div className='grid grid-cols-3 gap-2 rounded-lg border border-gray-6 bg-gray-2 p-2'>
          {DEV_TOOLS_POSITION_OPTIONS.map(({ value, label }) => {
            const isSelected = position === value;

            return (
              <label
                key={value}
                title={label}
                className={cn(
                  'flex h-16 cursor-pointer rounded-md border p-2 transition-colors',
                  'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary has-[:focus-visible]:outline-offset-1',
                  getDotAlignment(value),
                  isSelected ? 'border-primary bg-primary-3' : 'border-gray-6 bg-gray-1 hover:bg-gray-3'
                )}
              >
                <input type='radio' name='dev-tools-position' value={value} checked={isSelected} onChange={() => onPositionChange(value)} className='sr-only' />
                <span className='sr-only'>{label}</span>
                <span className={cn('size-2.5 rounded-full transition-colors', isSelected ? 'bg-primary' : 'bg-gray-8')} />
              </label>
            );
          })}
        </div>

        <Typography variant='caption-xs' color='secondary'>
          Ancorada em <strong>{currentLabel}</strong>.
        </Typography>
      </fieldset>
    </DevToolsPanel>
  );
};

export default PositionData;
